/**
 * Blog utilities for loading and parsing markdown posts
 */

import type { BlogPostMeta, BlogPost, BlogPostListItem } from '@/types/blog';

// Import all markdown files from the content/blog directory
const blogModules = import.meta.glob('/content/blog/**/*.md', { 
  eager: true, 
  query: '?raw',
  import: 'default' 
}) as Record<string, string>;

// Parse frontmatter from markdown content
function parseFrontmatter(content: string): { meta: Record<string, unknown>; content: string } {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = content.match(frontmatterRegex);
  
  if (!match) {
    return { meta: {}, content };
  }
  
  const [, frontmatter, body] = match;
  const meta: Record<string, unknown> = {};
  
  // Parse YAML-like frontmatter
  frontmatter.split('\n').forEach(line => {
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) return;
    
    const key = line.slice(0, colonIndex).trim();
    let value: unknown = line.slice(colonIndex + 1).trim();
    
    // Handle arrays (tags: [tag1, tag2])
    if (typeof value === 'string' && value.startsWith('[') && value.endsWith(']')) {
      value = value.slice(1, -1).split(',').map(s => s.trim().replace(/['"]/g, ''));
    }
    // Handle booleans
    else if (value === 'true') value = true;
    else if (value === 'false') value = false;
    // Remove quotes from strings
    else if (typeof value === 'string') {
      value = value.replace(/^['"](.*)['"]$/, '$1');
    }
    
    meta[key] = value;
  });
  
  return { meta, content: body.trim() };
}

// Get all blog posts metadata
export function getAllPosts(lang: 'zh' | 'en' = 'zh'): BlogPostListItem[] {
  const posts: BlogPostListItem[] = [];
  
  for (const [path, rawContent] of Object.entries(blogModules)) {
    const { meta } = parseFrontmatter(rawContent);
    
    // Skip unpublished posts
    if (meta.published === false) continue;
    
    // Extract slug from path: /content/blog/my-post.md -> my-post
    const slug = path.replace('/content/blog/', '').replace('.md', '');
    
    // Use localized content if available
    const title = (lang === 'en' && meta.titleEn) ? meta.titleEn as string : meta.title as string;
    const description = (lang === 'en' && meta.descriptionEn) ? meta.descriptionEn as string : meta.description as string;
    const tags = (lang === 'en' && meta.tagsEn) ? meta.tagsEn as string[] : (meta.tags as string[] || []);
    
    posts.push({
      slug,
      title: title || slug,
      description: description || '',
      date: meta.date as string || '',
      author: meta.author as string || '',
      tags,
      coverImage: meta.coverImage as string,
    });
  }
  
  // Sort by date descending
  return posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

// Get a single blog post by slug
export function getPostBySlug(slug: string, lang: 'zh' | 'en' = 'zh'): BlogPost | null {
  const path = `/content/blog/${slug}.md`;
  const rawContent = blogModules[path];
  
  if (!rawContent) return null;
  
  const { meta, content } = parseFrontmatter(rawContent);
  
  // Use localized content if available
  const title = (lang === 'en' && meta.titleEn) ? meta.titleEn as string : meta.title as string;
  const description = (lang === 'en' && meta.descriptionEn) ? meta.descriptionEn as string : meta.description as string;
  const tags = (lang === 'en' && meta.tagsEn) ? meta.tagsEn as string[] : (meta.tags as string[] || []);
  
  return {
    slug,
    title: title || slug,
    titleEn: meta.titleEn as string,
    description: description || '',
    descriptionEn: meta.descriptionEn as string,
    date: meta.date as string || '',
    author: meta.author as string || '',
    tags,
    tagsEn: meta.tagsEn as string[],
    coverImage: meta.coverImage as string,
    published: meta.published !== false,
    lang: meta.lang as 'zh' | 'en' | 'both',
    content,
    contentEn: meta.contentEn as string,
  };
}

// Get all unique tags
export function getAllTags(lang: 'zh' | 'en' = 'zh'): string[] {
  const posts = getAllPosts(lang);
  const tagSet = new Set<string>();
  
  posts.forEach(post => {
    post.tags.forEach(tag => tagSet.add(tag));
  });
  
  return Array.from(tagSet).sort();
}

